import { useCallback, memo, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import { FaArrowLeft, FaArrowRight, FaTimes } from "react-icons/fa";

const modalVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const contentVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, scale: 0.8, transition: { duration: 0.2 } },
};

const imageVariants = {
  enter: (direction) => ({
    opacity: 0,
    x: direction > 0 ? 20 : -20,
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4 },
  },
  exit: (direction) => ({
    opacity: 0,
    x: direction < 0 ? 20 : -20,
    transition: { duration: 0.4 },
  }),
};

const ImageModal = ({
  isOpen,
  onClose,
  panelImages,
  currentImageIndex,
  nextImage,
  prevImage,
  setImageIndex,
}) => {
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    if (isOpen && panelImages?.length) {
      panelImages.forEach((image) => {
        const img = new Image();
        img.src = image.src;
      });
    }
  }, [isOpen, panelImages]);

  const handlePrevClick = useCallback(
    (e) => {
      e.stopPropagation();
      setDirection(-1);
      prevImage();
    },
    [prevImage]
  );

  const handleNextClick = useCallback(
    (e) => {
      e.stopPropagation();
      setDirection(1);
      nextImage();
    },
    [nextImage]
  );

  const handleDotClick = useCallback(
    (index) => {
      setDirection(index > currentImageIndex ? 1 : -1);
      setImageIndex(index);
    },
    [currentImageIndex, setImageIndex]
  );

  if (!isOpen || !panelImages?.length) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={modalVariants}
          className="fixed inset-0 bg-white/95 z-50 flex items-center justify-center p-4"
          onClick={onClose}
          id="safePad"
        >
          <motion.div
            variants={contentVariants}
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overflow-hidden rounded-lg border border-gray-300 shadow-xl relative bg-white">
              <div
                className="relative overflow-hidden"
                style={{ height: "calc(80vh)" }}
              >
                <div className="w-full h-full flex items-center justify-center">
                  <AnimatePresence
                    initial={false}
                    custom={direction}
                    mode="crossfade"
                  >
                    <motion.div
                      key={currentImageIndex}
                      custom={direction}
                      variants={imageVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      className="absolute inset-0 w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={panelImages[currentImageIndex].src}
                        alt={panelImages[currentImageIndex].alt}
                        className="max-h-full max-w-full object-contain"
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex justify-between px-4">
                <motion.button
                  onClick={handlePrevClick}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-3 rounded-full shadow-lg cursor-pointer"
                >
                  <FaArrowLeft className="text-sm" />
                </motion.button>
                <motion.button
                  onClick={handleNextClick}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-3 rounded-full shadow-lg cursor-pointer"
                >
                  <FaArrowRight className="text-sm" />
                </motion.button>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="text-gray-800 font-medium">
                {panelImages[currentImageIndex].alt}
              </div>
              <div className="flex gap-3 items-center">
                <div className="text-gray-700 text-sm font-medium">
                  {currentImageIndex + 1} / {panelImages.length}
                </div>
                <motion.button
                  onClick={onClose}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 p-2 rounded-full shadow-lg cursor-pointer"
                >
                  <FaTimes className="text-sm" />
                </motion.button>
              </div>
            </div>

            <div className="flex justify-center mt-4 gap-2">
              {panelImages.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  className={`w-3 h-3 rounded-full ${
                    index === currentImageIndex
                      ? "bg-blue-600"
                      : "bg-gray-500 cursor-pointer"
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default memo(ImageModal);

ImageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  panelImages: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ).isRequired,
  currentImageIndex: PropTypes.number.isRequired,
  nextImage: PropTypes.func.isRequired,
  prevImage: PropTypes.func.isRequired,
  setImageIndex: PropTypes.func.isRequired,
};
